/**
 * French messages of the household feature (household sheet, registration
 * door, my follow-ups). Merged into the catalogue by `src/i18n/fr.ts` under
 * the `household` key.
 */
export const householdMessages = {
  /**
   * The "carte jaune" shown for a phantom door
   * (`docs/maquettes/prototype-terrain.html`'s "Porte inscription" screen),
   * on the household sheet and the registration door.
   */
  phantomCard: {
    title: 'Probablement non inscrit',
    confidence: 'Confiance {{confidence}}',
    generic:
      'Vérifiez sur place si le logement est habité : boîte aux lettres, sonnette, voisinage.',
  },
  sheet: {
    engagementFirstName: 'Prénom de la personne (avec son accord)',
    engagementFirstNameRequired: "Indiquez le prénom de la personne qui s'engage.",
    noAssignment: {
      title: 'Aucune porte attribuée',
      body: 'Vous ne détenez aucune porte pour le moment. Retournez à la session pour en recevoir une.',
      back: 'Retour à la session',
    },
    loadError: 'Impossible de charger la porte. Réessayez dans un instant.',
    canvassTargetBadge: 'Cible PAP',
    registrationTargetBadge: 'Cible inscription',
    householdKicker: 'Foyer · {{count}} inscrit{{plural}}',
    householdEmpty: 'Aucun inscrit connu à cette adresse.',
    ageBandUnknown: 'âge inconnu',
    turnout: {
      voted: 'A voté',
      abstained: "N'a pas voté",
      unknown: 'Indéterminé',
    },
    lastVisit: 'Dernière visite : {{date}} — {{outcome}} ({{author}})',
    outcomeKicker: 'Résultat de la visite',
    outcomeRequired: 'Choisissez le résultat de la visite.',
    qualityKicker: "Qualité de l'échange",
    qualityHint:
      "Qualité de l'échange, pas de la personne : sert à prioriser les repassages et à repérer les immeubles où on accroche.",
    qualityRequired: "La qualité de l'échange est obligatoire pour ce résultat.",
    doNotVisit: 'Ne plus visiter ce foyer',
    noteReminder: 'Aucune opinion politique dans la note — relue par l’admin.',
    notePlaceholder: 'Ex. : repasser après 18 h, sonnette en panne…',
    noteLabel: 'Note du foyer',
    engagementsKicker: 'Engagements · avec la personne',
    consent: 'Chaque case se coche devant la personne, qui voit l’écran. Horodaté, tracé.',
    submit: 'Enregistrer → suivant',
    submitting: 'Enregistrement…',
    invalidVisit: "L'enregistrement de la visite a échoué.",
    queuedVisit: 'Visite enregistrée · en attente d’envoi.',
  },
  outcomes: {
    absent: 'Absent',
    exchange: 'Échange fait',
    refusal: 'Refus',
    come_back: 'À revoir',
    registration_info: 'Infos inscription remises',
    moved: 'Déménagé',
    phantom_absent_unit: 'Logement vide / n’existe pas',
  },
  stars: {
    groupLabel: "Qualité de l'échange, de 1 à 5 étoiles",
    optionLabel: '{{n}} étoile{{plural}} sur 5',
    readout: '{{n}} / 5 — {{label}}',
    label1: 'porte entrouverte, échange écourté',
    label2: 'échange bref',
    label3: 'échange correct, à confirmer',
    label4: 'bon échange, personne réceptive',
    label5: 'échange nourri, contact chaud',
  },
  engagements: {
    contact: {
      label: 'Prise de contact',
      emailPlaceholder: 'n.fabre@exemple.fr',
      phonePlaceholder: '06 …',
    },
    newsletter: {
      label: 'Newsletter locale',
      hint: 'Double opt-in : e-mail de confirmation envoyé',
    },
    support_campaign: {
      label: 'Soutenir la campagne',
      hint: 'Consentement explicite requis',
    },
    building_referent: {
      label: 'Référent·e d’immeuble',
      hint: 'Un immeuble sans référent·e en profite en premier',
    },
    join_group: {
      label: 'Rejoindre le groupe d’action',
      hint: 'Consentement explicite requis',
    },
    error: "L'engagement n'a pas pu être enregistré.",
  },
  registration: {
    title: 'Porte inscription',
    metaLabel: 'PAP inscription',
    constatKicker: 'Constat',
    outcomes: {
      info_given: 'Habité · infos inscription remises',
      already_registered: 'Habité · déjà inscrit ailleurs',
      refusal: 'Habité · refus',
      empty: 'Logement vide / n’existe pas',
    },
    constatRequired: 'Choisissez un constat.',
    relanceKicker: 'Relance automatique',
    consent:
      'L’e-mail est le canal de relance : la personne le donne devant vous, en sachant qu’elle recevra jusqu’à trois messages d’aide à l’inscription et pourra se désabonner en un clic.',
    consentCheckbox: 'J’ai le consentement explicite de la personne',
    firstNameLabel: 'Prénom',
    emailLabel: 'E-mail',
    phoneLabel: 'Téléphone (second canal)',
    phoneHint:
      'Le numéro sert de second canal quand la personne n’a pas d’e-mail. La relance SMS s’activera quand la passerelle sera branchée — d’ici là, le numéro n’est utilisé par personne.',
    sequenceKicker: 'Séquence prévue',
    sequenceJ2: 'Mode d’emploi de l’inscription + pièces à réunir',
    sequenceJ7: 'Rappel : « où en êtes-vous ? » + permanence du samedi',
    sequenceJ14: 'Dernier rappel avant la clôture des inscriptions',
    reminder: 'Aucune opinion politique. Pas de nom relevé sans accord.',
    submit: 'Enregistrer → suivant',
    continue: 'Continuer',
    consentRequiredError: 'Le consentement est obligatoire pour ouvrir ce dossier.',
    messagesPlanned: '{{count}} message{{plural}} planifié{{plural}}',
    queuedCase: 'Dossier enregistré · en attente d’envoi.',
    queuedBody:
      'Vous verrez la séquence de relance planifiée une fois la connexion revenue et le dossier envoyé.',
  },
  cases: {
    title: 'Mes relances',
    lead: 'Infos remises → dossier déposé → confirmé au REU. Les mails partent automatiquement (J+2, J+7, J+14) ; vous voyez ce qui a été envoyé et ce qui a répondu.',
    empty: 'Aucun dossier de relance pour le moment.',
    loadError: 'Impossible de charger vos relances. Réessayez dans un instant.',
    openedOn: 'Infos remises le {{date}}',
    stage: {
      info_given: 'Infos remises',
      filed: 'Dossier déposé',
      confirmed: 'Confirmé au REU',
      closed: 'Clos',
    },
    messageStatus: {
      scheduled: 'Planifié',
      sent: 'Envoyé',
      opened: 'Ouvert',
      replied: 'Répondu',
      unsubscribed: 'Désabonné',
      failed: 'En erreur',
      cancelled: 'Annulé',
    },
    smsGatewayBadge: 'Passerelle à brancher',
    messageDate: 'le {{date}}',
    step: {
      j2: 'J+2',
      j7: 'J+7',
      j14: 'J+14',
    },
    noEmail: 'Aucune — pas d’adresse',
    repliedDetail: {
      filed: 'a répondu : dossier déposé',
      registered: 'a répondu : déjà inscrit·e',
      generic: 'a répondu',
    },
    unsubscribedDetail: 's’est désabonné·e',
    cancelledReason: {
      filed: 'séquence arrêtée : dossier déposé',
      confirmed: 'séquence arrêtée : inscription confirmée',
      closed: 'séquence arrêtée : dossier clos',
      unsubscribed: 'séquence arrêtée : désabonnement',
    },
    failedDetail: 'Envoi en échec',
    rearm: {
      button: 'Réarmer',
      conflict: 'Ce message n’est plus en échec.',
      error: 'Le réarmement a échoué.',
    },
    actions: {
      filed: 'Dossier déposé',
      confirmed: 'Confirmé',
      close: 'Clore',
    },
    closeReasonTitle: 'Motif de clôture',
    closeReasonSubmit: 'Clore le dossier',
    closeReason: {
      confirmed: 'Inscription confirmée',
      unsubscribed: 'Désabonnement',
      no_answer: 'Sans réponse',
      withdrawn: 'Retrait du consentement',
      other: 'Autre',
    },
    advanceError: 'Cette étape ne peut pas revenir en arrière.',
  },
} as const;
